/* Built In Imports */
import { useContext, useEffect } from 'react';

/* External Imports */
import {
  Box,
  Button,
  Heading,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Text,
  useBreakpointValue,
  useDisclosure,
} from '@chakra-ui/react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';

/* Internal Imports */
/* Components */
import config from '@config';
import PageConfig from '@lb/Common/PageConfig';
import WrapLayout from '@lb/components/Layout/WrapLayout';
import Breadcrum from '@lb/components/Navigation/Breadcrum';
import HeadComponent from '@lb/components/Utility/HeadComponent';
import { AuthContext } from '@store/auth-context';

/* Services */
import { getLocale } from '@msr/components/Utility/Shared/SharedService';

import {
  fetchFooterDetailData,
  fetchHeaderMenuData,
} from 'services/commonService';
import { fetchDetails, getLandingPaths } from 'services/lbService';

/**
 *
 * @param {object} pageData
 * @param lang
 * @param region
 * @returns
 */

const AboutDevi = ({ pageData, region, lang }) => {
  const authContext = useContext(AuthContext);
  const { isOpen, onOpen, onClose } = useDisclosure();
  useEffect(() => {
    authContext.RegionAndLang(region, lang);
  }, [authContext, region, lang]);

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);

    let mm = gsap.matchMedia();

    gsap.set('.loadingBlock', { autoAlpha: 1 });
    gsap.set(['.section-1 .section-1__bg-wrapper img'], { autoAlpha: 0 });
    gsap.set(
      [
        '.section-1 .section-1__heading',
        '.section-1 .section-1__text-1',
        '.section-1 .section-1__text-2',
      ],
      { autoAlpha: 0, y: '3rem' }
    );
    gsap.set('.section-1__sadhguru-imgs img', { autoAlpha: 0, y: '50%' });

    const tlHeroLoading = gsap.timeline({ defaults: { duration: 0.75 } });
    tlHeroLoading
      .to('.loadingBlock', { autoAlpha: 0, delay: 1 })
      .to('.section-1 .section-1__heading', { autoAlpha: 1, y: '0rem' })
      .to(
        '.section-1 .section-1__text-1',
        { autoAlpha: 1, y: '0rem' },
        '>-0.25'
      )
      .to('.section-1 .img-backdrop', { autoAlpha: 1 })
      .to('.section-1 .bg--layer-1', { autoAlpha: 1 }, '>-0.25')
      .to('.section-1 .img-linga', { autoAlpha: 1 }, '>-0.25');

    const pageLoadingBar = gsap.from('.page-progress-bar', {
      width: '0%',
      ease: 'none',
      scrollTrigger: {
        scrub: 1,
      },
    });

    const pageScrollIcon = gsap.to('.page-scroll-icon-wrap', {
      autoAlpha: 0,
      scrollTrigger: {
        trigger: '.section-1',
        start: '25% center',
        // markers: true,
        toggleActions: 'play none none reverse',
      },
    });

    const tlSection1 = gsap.timeline({
      scrollTrigger: {
        trigger: '.section-1',
        start: 'top top',
        end: 'bottom bottom',
        // markers: true,
        scrub: 1.2,
      },
    });

    mm.add('(min-width: 1200px)', () => {
      tlSection1
        .to('.section-1 .img-sadhguru', { autoAlpha: 1 })
        .to('.section-1 .section-1__text-1', { autoAlpha: 0, y: '-3rem' })
        .to('.section-1 .section-1__text-2', { autoAlpha: 1, y: '0rem' })
        .to('.section-1 .img-hands', { autoAlpha: 1 })
        .to('.section-1 .img-eyes', { autoAlpha: 1 })
        .to('.section-1__content', { autoAlpha: 0 }, '>0.5')
        .to('.section-1 .img-sadhguru', { autoAlpha: 0 }, '>-0.25')
        .to('.section-1__bg-wrapper', { autoAlpha: 0.4 })
        .to('.section-1__sadhguru-imgs', { autoAlpha: 1 })
        .to(
          '.section-1__sadhguru-imgs img',
          { autoAlpha: 1, y: '0%', stagger: 0.25 },
          '<'
        )
        .to('.section-1__bg-wrapper', { duration: 0.5 });
    });

    mm.add('(max-width: 1199.99px)', () => {
      tlSection1
        .to('.section-1 .img-sadhguru', { autoAlpha: 1 })
        .to('.section-1 .section-1__text-1', { autoAlpha: 0, y: '-3rem' })
        .to('.section-1 .section-1__text-2', { autoAlpha: 1, y: '0rem' })
        .to('.section-1 .img-hands', { autoAlpha: 1 })
        .to('.section-1 .img-eyes', { autoAlpha: 1 })
        .to('.section-1 .img-sadhguru', { autoAlpha: 0 }, '>-0.25')
        .to('.section-1__bg-wrapper', { autoAlpha: 0.4 })
        .to('.section-1__sadhguru-imgs', { autoAlpha: 1 })
        .to(
          '.section-1__sadhguru-imgs img',
          { autoAlpha: 1, y: '0%', stagger: 0.25 },
          '<'
        )
        .to('.section-1__sadhguru-imgs img', { duration: 2 })
        .to('.section-1__bg-wrapper', { duration: 0.5 });
    });

    gsap.set('.section-2__scroll-heading', { autoAlpha: 0, scale: 0 });
    gsap.set('.section-2__wrapper.part-2', { autoAlpha: 0 });

    const tlSection2 = gsap.timeline({
      defaults: { duration: 2 },
      scrollTrigger: {
        trigger: '.section-2',
        start: 'top top',
        end: 'bottom bottom',
        // markers: true,
        scrub: 1.2,
      },
    });

    tlSection2
      .to('.section-2__scroll-heading:nth-child(1)', { autoAlpha: 1, scale: 1 })
      .to('.section-2__scroll-heading:nth-child(2)', { autoAlpha: 1, scale: 1 })
      .to('.section-2__scroll-heading:nth-child(1)', { autoAlpha: 0, scale: 0 })
      .to('.section-2__scroll-heading:nth-child(3)', { autoAlpha: 1, scale: 1 })
      .to('.section-2__scroll-heading:nth-child(2)', { autoAlpha: 0, scale: 0 })
      .to('.section-2__scroll-heading:nth-child(4)', { autoAlpha: 1, scale: 1 })
      .to('.section-2__scroll-heading:nth-child(3)', { autoAlpha: 0, scale: 0 })
      .to('.section-2__scroll-heading:nth-child(5)', { autoAlpha: 1, scale: 1 })
      .to('.section-2__scroll-heading:nth-child(4)', { autoAlpha: 0, scale: 0 })
      .to('.section-2__scroll-heading:nth-child(6)', { autoAlpha: 1, scale: 1 })
      .to('.section-2__scroll-heading:nth-child(5)', { autoAlpha: 0, scale: 0 })
      .to('.section-2__scroll-heading:nth-child(7)', { autoAlpha: 1, scale: 1 })
      .to('.section-2__scroll-heading:nth-child(6)', { autoAlpha: 0, scale: 0 })
      .to('.section-2__scroll-heading:nth-child(7)', { autoAlpha: 0, scale: 0 })
      .to(
        '.section-2__wrapper.part-1',
        { duration: 2, backgroundColor: 'rgba(0,0,0,0.6)' },
        '<'
      )
      .to('.section-2__wrapper.part-2', { duration: 2, autoAlpha: 1 })
      .to('.section-2__wrapper.part-2', { duration: 2 });

    const tlSection3 = gsap.timeline({
      scrollTrigger: {
        trigger: '.section-3',
        start: 'top bottom',
        end: 'bottom bottom',
        // markers: true,
        scrub: 1.2,
      },
    });

    tlSection3
      .from('.section-3 .section-3__bg-img', { scale: 2, duration: 4 })
      .to(
        '.section-3__img-upasaka',
        { autoAlpha: 1, scale: 1, duration: 1 },
        '>-2'
      )
      .from(
        '.section-3__content-wrapper.part-1',
        { autoAlpha: 0, y: '3rem' },
        '<0.5'
      )
      .to(
        '.section-3__content-wrapper.part-1',
        { autoAlpha: 0, y: '-3rem' },
        '>1'
      )
      .to('.section-3 .section-3__bg-img', { opacity: 0.4 }, '<0.5')
      .to('.section-3__img-upasaka', { autoAlpha: 0 }, '<')
      .to(
        '.section-3__img-trishul',
        { autoAlpha: 1, scale: 1, left: '50%' },
        '<0.25'
      )
      .to('.section-3__img-devotee', { autoAlpha: 1, scale: 1 }, '<0.25')
      .to('.section-3__img-trishul', { autoAlpha: 0.7, left: '50%' })
      .from('.section-3__content-wrapper.part-2', { autoAlpha: 0, scale: 1.2 })
      .to(['.section-3__upasaka-imgs', '.section-3 .section-3__bg-img'], {
        autoAlpha: 0,
      })
      .to(
        '.section-3__content-wrapper.part-2',
        { autoAlpha: 0, y: '-5rem' },
        '>0.5'
      )
      .to('.section-3__bg-cover-img', { autoAlpha: 1 }, '<')
      .from('.section-3__content-wrapper.part-3', { autoAlpha: 0, y: '5rem' })
      .from(
        '.section-3__sadhguru-imgs img',
        { autoAlpha: 0, y: '25%', stagger: 0.25 },
        '<'
      )
      .to(
        '.section-3__content-wrapper.part-3',
        { autoAlpha: 0, y: '-5rem' },
        '>0.5'
      )
      .to(
        '.section-3__sadhguru-imgs img',
        { autoAlpha: 0, y: '-25%', stagger: 0.15 },
        '<'
      )
      .from('.section-3__bg-devi-chakras', { autoAlpha: 0, y: '20%' }, '<0.5')
      .from(
        '.section-3__content-wrapper.part-4',
        { autoAlpha: 0, y: '25%' },
        '<0.25'
      )
      .from(
        '.section-3__chakra-item',
        { autoAlpha: 0, y: '25%', stagger: 0.25 },
        '<0.5'
      )
      .to(
        '.section-3__content-wrapper.part-4',
        { autoAlpha: 0, y: '-25%' },
        '>0.5'
      )
      .from(
        '.section-3__content-wrapper.part-5',
        { autoAlpha: 0, y: '25%' },
        '<0.25'
      )
      .to(
        '.section-3__content-wrapper.part-5',
        { autoAlpha: 0, y: '-25%' },
        '>0.5'
      )
      .to('.section-3__bg-devi-chakras', { autoAlpha: 0 }, '<0.25')
      .to('.section-3__bg-dandams', { autoAlpha: 1 }, '<0.25')
      .from('.section-3__content-wrapper.part-6', { autoAlpha: 0, y: '15%' })
      .to('.section-3__content-wrapper.part-6', { duration: 1 });

    const tlSection4 = gsap.timeline({
      scrollTrigger: {
        trigger: '.section-4',
        start: 'top bottom',
        end: 'bottom bottom',
        // markers: true,
        scrub: 1.2,
      },
    });

    tlSection4
      .from('.section-4 .section-4__bg-img.bg-1', { scale: 2, duration: 4 })
      .from(
        '.section-4__content-wrapper.part-1',
        { autoAlpha: 0, y: '3rem' },
        '>-2'
      )
      .to(
        '.section-4__content-wrapper.part-1',
        { autoAlpha: 0, y: '-3rem' },
        '>1'
      )

      .from('.section-4__content-wrapper.part-2', { autoAlpha: 0 }, '<0.25')
      .from(
        '.section-4__content-wrapper.part-2 img',
        { autoAlpha: 0, y: '25%', stagger: 0.5 },
        '<'
      )
      .to(
        '.section-4__content-wrapper.part-2 img',
        { autoAlpha: 0, y: '-25%', stagger: 0.25 },
        '>1'
      )
      .to('.section-4__content-wrapper.part-2', { autoAlpha: 0, y: '-5rem' })
      .from(
        '.section-4__content-wrapper.part-3',
        { autoAlpha: 0, y: '5rem' },
        '<'
      )
      .to(
        '.section-4__content-wrapper.part-3',
        { autoAlpha: 0, y: '-5rem' },
        '>2'
      )
      .to('.section-4 .section-4__bg-img.bg-2', { autoAlpha: 0.2 }, '<0.5')
      .to('.section-4 .section-4__bg-img.bg-1', { autoAlpha: 0 }, '<')
      .from(
        '.section-4__content-wrapper.part-4',
        { autoAlpha: 0, y: '25%' },
        '<0.5'
      )
      .to(
        '.section-4__content-wrapper.part-4',
        { autoAlpha: 0, y: '-25%' },
        '>1'
      )
      .to('.section-4 .section-4__bg-img.bg-2', { autoAlpha: 1 })
      .from(
        '.section-4__content-wrapper.part-5',
        { autoAlpha: 0, y: '25%' },
        '<0.5'
      )
      .to(
        '.section-4__content-wrapper.part-5',
        { autoAlpha: 0, y: '-25%' },
        '>1.5'
      )
      .to('.section-4 .section-4__bg-img.bg-2', { autoAlpha: 0.2 }, '<')

      .from('.section-4__content-wrapper.part-6', { autoAlpha: 0, y: '25%' })
      .from(
        '.section-4__content-wrapper.part-6 .img-1',
        { autoAlpha: 0, y: '25%' },
        '<0.5'
      )
      .from(
        '.section-4__content-wrapper.part-6 .img-2',
        { autoAlpha: 0, y: '25%' },
        '<0.5'
      )
      .from(
        '.section-4__content-wrapper.part-6 .img-3',
        { autoAlpha: 0, y: '25%' },
        '<0.5'
      )
      .to('.section-4__content-wrapper.part-6', { duration: 1 });

    const tlSection5 = gsap.timeline({
      scrollTrigger: {
        trigger: '.section-5',
        start: 'top bottom',
        end: 'bottom bottom',
        scrub: 1.2,
      },
    });

    tlSection5
      .from('.section-5 .section-5__bg-img', { scale: 1.5, duration: 4 })
      .from('.section-5__content-wrapper', { autoAlpha: 0, y: '3rem' }, '>-2')
      .to('.section-5__content-wrapper', { autoAlpha: 0, y: '-3rem' }, '>1')
      .to('.section-5 .section-5__bg-img', { autoAlpha: 1 })
      .to('.section-5 .section-5__bg-img', { duration: 1 });

    const tlSection6 = gsap.timeline({
      scrollTrigger: {
        trigger: '.section-6',
        start: 'top bottom',
        end: 'bottom bottom',
        scrub: 1.2,
      },
    });

    tlSection6
      .from('.section-6 .section-6__bg-img.bg-1', { scale: 1.5, duration: 2 })
      .to('.section-6 .section-6__bg-img.bg-2', { autoAlpha: 1, duration: 2 })
      .from('.section-6__content-wrapper', { autoAlpha: 0, y: '3rem' })
      .to('.section-6 .section-6__bg-img.bg-2', { duration: 1 });

    const tlSection7 = gsap.timeline({
      scrollTrigger: {
        trigger: '.section-7',
        start: 'top bottom',
        end: 'bottom bottom',
        scrub: 1.2,
      },
    });

    tlSection7
      .from(
        '.section-7__content-wrapper.part-1',
        { autoAlpha: 0, y: '3rem' },
        '<3'
      )
      .to(
        '.section-7__content-wrapper.part-1',
        { autoAlpha: 0, y: '-3rem' },
        '>2'
      )
      .from(
        '.section-7__content-wrapper.part-2',
        { autoAlpha: 0, y: '3rem' },
        '<0.5'
      )
      .to('.section-7 .section-7__bg-img', { autoAlpha: 0.2, duration: 2 }, '<')
      .to(
        '.section-7__content-wrapper.part-2',
        { autoAlpha: 0, y: '-3rem' },
        '>1'
      )
      .to('.section-7 .section-7__bg-img', { autoAlpha: 1, duration: 2 })
      .to('.section-7 .section-7__bg-img', { duration: 1 });
  }, []);

  const section = pageData?.body;

  return (
    <WrapLayout pageData={pageData}>
      <HeadComponent
        data={pageData?._seoMetaTags || pageData?.seoFields}
        availableLocals={pageData?.availableLocales}
      >
        {' '}
      </HeadComponent>
      {pageData?.breadcrumbs?.length > 0 && (
        <Breadcrum
          page={pageData.breadcrumbs}
          alsoin={pageData.alsoin}
          url=""
          region={region}
          lang={lang}
        />
      )}

      <Box
        className="page-progress-bar"
        pos="fixed"
        top={0}
        left={0}
        width="100%"
        height=".625rem"
        bgGradient="linear(to-r, #E85B4C, #f6a15e)"
        zIndex={99999}
        // opacity="0"
        // visibility="hidden"
      ></Box>

      <Box
        className="loadingBlock"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        p="50px 20px"
        bg="black"
        color="#fff"
        fontSize="40px"
        h={'100%'}
        pos={'fixed'}
        inset={0}
        zIndex={99999}
        opacity={0}
        visibility={'hidden'}
      >
        <Box h="100px" w="100px" marginBottom="20px" pos="relative">
          <Box
            bg="#D9D9D9"
            h="100%"
            w="100%"
            borderRadius="50%"
            border={'8px solid #444'}
            pos="absolute"
            animation={
              'loadingDotMotion 1s cubic-bezier(0.55, 0.06, 0.68, 0.19) infinite alternate'
            }
          ></Box>
        </Box>
        Loading
      </Box>

      <Box
        className="page-scroll-icon-wrap"
        pos="fixed"
        bottom={'1rem'}
        left={'50%'}
        width="4.375rem"
        maxW={'10vw'}
        height="4.375rem"
        zIndex={99}
        transform={'translateX(-50%)'}
      >
        <svg
          width="70"
          height="70"
          viewBox="0 0 70 70"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M18 20.2192C18 10.7093 25.6112 3 35 3C44.3888 3 52 10.7093 52 20.2192V34.8082C52 44.3181 44.3888 52.0274 35 52.0274C25.6112 52.0274 18 44.3181 18 34.8082V20.2192ZM35 6.80147C27.6839 6.80147 21.7531 12.8088 21.7531 20.2192V34.8082C21.7531 42.2186 27.6839 48.2259 35 48.2259C42.3161 48.2259 48.2469 42.2186 48.2469 34.8082V20.2192C48.2469 12.8088 42.3161 6.80147 35 6.80147Z"
            fill="white"
          />
          <path
            className="scroll-icon-arrow"
            d="M44.1624 56.2442L46.2443 59.4072L35 67L23.7557 59.4072L25.8376 56.2442L35 62.4312L44.1624 56.2442Z"
            fill="white"
          />
          <path
            className="scroll-icon-line"
            d="M36.8765 25.7672H33.1234V14.6712H36.8765V25.7672Z"
            fill="white"
          />
        </svg>
      </Box>

      <Box as="main" bg="black" color="white" overflow="clip">
        <Box
          as="section"
          className="section-1"
          h={{ base: '350vh', lg: '700vh' }}
          pos={'relative'}
        >
          <Box h={'850vh'}>
            <Box
              className="section-1__wrapper"
              h={{ md: '100vh' }}
              minH={'100vh'}
              py={{ base: 8, sm: 16, xl: 24, '2xl': 32 }}
              bg={'black'}
              pos={'sticky'}
              top={0}
              display="flex"
              flexDirection="column"
              justifyContent={{
                base: 'flex-end',
                md: 'center',
                xl: 'flex-end',
              }}
            >
              <Box
                className="page-container"
                display={'flex'}
                alignItems={{ md: 'center' }}
                flexDirection={{ base: 'column', md: 'row' }}
              >
                <Box
                  className="section-1__content"
                  maxW={{ base: 200, md: 300, xl: 350 }}
                  display="grid"
                >
                  <Heading
                    className="section-1__heading"
                    as="H1"
                    color="#E85B4C"
                    fontSize={{ base: '64px', md: '100px', xl: '150px' }}
                    lineHeight={1}
                    fontFamily="'FedraSerifDisplay'"
                    textTransform="uppercase"
                  >
                    {section?.[0].sectionContent?.[0]?.text}
                  </Heading>
                  <Text
                    className="section-1__text-1"
                    color="#FAEFE7"
                    fontSize={{ base: '16px', md: '18px', xl: '24px' }}
                    pb={3}
                    gridArea={{ md: '2/1' }}
                    order={{ base: '-1', md: 'unset' }}
                  >
                    {section?.[0]?.sectionContent?.[1]?.text}
                  </Text>
                  <Text
                    className="section-1__text-2"
                    color="#FAEFE7"
                    fontSize={{ base: '12px', md: '18px', xl: '24px' }}
                    gridArea={{ md: '2/1' }}
                  >
                    {section?.[0]?.sectionContent?.[2]?.text}
                  </Text>
                </Box>

                <Box
                  className="section-1__sadhguru-imgs"
                  pos={{ xl: 'absolute' }}
                  inset="0"
                  h={{ xl: '100%' }}
                  w={'100%'}
                  py={'2rem'}
                  ml={'auto'}
                  display={'flex'}
                  flexDirection={'column'}
                  justifyContent={'center'}
                  zIndex={-1}
                  opacity={0}
                  visibility={'hidden'}
                >
                  <Box
                    // maxW={{base:820, '2xl':1120}}
                    maxW={1120}
                    w={'100%'}
                    mx={'auto'}
                    px={'1.25rem'}
                  >
                    <Image
                      className="img-sadhguru-1"
                      src={`${config.imgPath}/d/46272/1689868404-20100129_chi_0529-e.jpg`}
                      display="block"
                      w={{ base: '100%', sm: '62%' }}
                      ml={{ md: 'auto' }}
                      mr={{ md: '3%' }}
                    ></Image>
                    <Image
                      className="img-sadhguru-2"
                      src={`${config.imgPath}/d/46272/1684475443-lb_sadhguru_img4.png`}
                      display="block"
                      w={{ base: '75%', sm: '42.5%' }}
                      mt={{ base: '-10%', sm: '-35%', md: '-5%' }}
                      ml={{ base: 'auto', xl: 'unset' }}
                      mr={{ base: 'auto', sm: 'unset' }}
                      pos={'relative'}
                      zIndex={1}
                    ></Image>
                    <Image
                      className="img-sadhguru-3"
                      src={`${config.imgPath}/d/46272/1690208029-whatsapp-image-2023-07-24-at-19-38-12-edited.png`}
                      w={'53%'}
                      ml={'auto'}
                      mr={'6%'}
                      mt={'-25%'}
                      display={{ base: 'none', xl: 'block' }}
                    ></Image>
                  </Box>
                </Box>
              </Box>

              <Box
                className="section-1__bg-wrapper"
                pos="absolute"
                inset="0"
                h={'100%'}
                w={'100%'}
                zIndex={-2}
              >
                <Image
                  className="img-backdrop"
                  src={`${config.imgPath}/d/46272/1684473355-ib_secion1bg.jpg`}
                  pos="absolute"
                  inset={0}
                  w={'100%'}
                  h={'100%'}
                  objectFit={'cover'}
                  objectPosition={'top'}
                ></Image>
                <Image
                  className="bg--layer-1 img-yantra"
                  src={`${config.imgPath}/d/46272/1684474079-lb_yantra.png`}
                  pos="absolute"
                  top={0}
                  left={'50%'}
                  transform={'translateX(-50%)'}
                  w={'40%'}
                  maxWidth={220}
                ></Image>
                <Image
                  className="bg--layer-1 img-sun"
                  src={`${config.imgPath}/d/46272/1684474013-lb_sun.png`}
                  pos="absolute"
                  top={'10%'}
                  left={'15%'}
                  transform={'translateX(-50%)'}
                  w={'20%'}
                  maxWidth={330}
                ></Image>
                <Image
                  className="bg--layer-1 img-moon"
                  src={`${config.imgPath}/d/46272/1684475466-lb_moon.png`}
                  pos="absolute"
                  top={'10%'}
                  right={'15%'}
                  transform={'translateX(50%)'}
                  w={'20%'}
                  maxWidth={330}
                ></Image>
                <Box
                  className="devi-n-sadhguru-block"
                  pos="absolute"
                  bottom={{ md: '-5%' }}
                  top={{ base: '10%', md: 'unset' }}
                  left={{ base: '65%', md: '50%' }}
                  transform={'translateX(-50%)'}
                  w={{ base: '80%', md: '100%' }}
                  maxH={'95%'}
                  sx={{ aspectRatio: 0.98 }}
                >
                  <Image
                    className="img-linga"
                    src={`${config.imgPath}/d/46272/1684475388-lb_linga.png`}
                    pos="absolute"
                    top={'0%'}
                    left={'50%'}
                    transform={'translateX(-50%)'}
                    h={'100%'}
                    maxWidth={'none'}
                  ></Image>
                  <Image
                    className="img-hands"
                    src={`${config.imgPath}/d/46272/1684475581-lb_hands.png`}
                    pos="absolute"
                    top={'26%'}
                    left={'50%'}
                    transform={'translateX(-50%)'}
                    h={'62%'}
                    maxWidth={'none'}
                  ></Image>
                  <Image
                    className="img-eyes"
                    src={`${config.imgPath}/d/46272/1684475597-lb_eyes.png`}
                    pos="absolute"
                    top={'1%'}
                    left={'50%'}
                    transform={'translateX(-50%)'}
                    h={'80%'}
                    maxWidth={'none'}
                  ></Image>
                  <Image
                    className="img-sadhguru"
                    src={`${config.imgPath}/d/46272/1684475353-lb_mob_sadhguru.png`}
                    pos="absolute"
                    top={'17%'}
                    left={'50%'}
                    transform={'translateX(-56%)'}
                    h={'109%'}
                    maxWidth={'none'}
                  ></Image>
                  <Box
                    pos={'absolute'}
                    left={0}
                    top={'50%'}
                    height={'100vh'}
                    width={'100vw'}
                    bgGradient="linear(#0000 0vw, #000f 63vw, black 100vw)"
                    display={{ base: 'block', md: 'none' }}
                    transform={'translateX(-25%)'}
                  ></Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box
          as="section"
          className="section-2"
          h={{ base: '250vh', lg: '500vh' }}
          pos={'relative'}
        >
          <Box
            h={'650vh'}
            sx={{
              clipPath:
                'polygon(0% 20vw, 50% 0%, 100% 20vw, 100% 100%, 0% 100%)',
            }}
            backgroundColor={'black'}
          >
            <Image
              src={`${config.imgPath}/d/46272/1684474022-ib_secion2bg.png`}
              display="block"
              w={'100%'}
              h={'100vh'}
              objectFit={'cover'}
              position={'fixed'}
              inset={0}
              zIndex={-1}
            ></Image>
            <Box
              className="section-2__wrapper part-1"
              h={{ md: '100vh' }}
              minH={'100vh'}
              py={20}
              pos={'sticky'}
              top={0}
              display="flex"
              flexDirection="column"
              justifyContent="center"
            >
              <Box
                className="page-container"
                display="flex"
                flexDirection="column"
              >
                <Heading
                  className="section-2__scroll-heading"
                  display="inline-flex"
                  color="white"
                  fontFamily="'FedraSerifDisplay'"
                  fontSize={{
                    base: '2rem',
                    md: '3.5rem',
                    xl: '5rem',
                    '2xl': '6.5rem',
                  }}
                  marginLeft="20%"
                  marginRight="auto"
                >
                  Refined
                </Heading>
                <Heading
                  className="section-2__scroll-heading"
                  display="inline-flex"
                  color="white"
                  fontFamily="'FedraSerifDisplay'"
                  fontSize={{
                    base: '2rem',
                    md: '3.5rem',
                    xl: '5rem',
                    '2xl': '6.5rem',
                  }}
                  marginLeft="auto"
                  marginRight="10%"
                >
                  {section?.[1]?.sectionContent?.[0]?.data?.title2}
                </Heading>
                <Heading
                  className="section-2__scroll-heading"
                  display="inline-flex"
                  color="white"
                  fontFamily="'FedraSerifDisplay'"
                  fontSize={{
                    base: '2rem',
                    md: '3.5rem',
                    xl: '5rem',
                    '2xl': '6.5rem',
                  }}
                  marginLeft="10%"
                  marginRight="auto"
                >
                  {section?.[1]?.sectionContent?.[0]?.data?.title3}
                </Heading>
                <Heading
                  className="section-2__scroll-heading"
                  display="inline-flex"
                  color="white"
                  fontFamily="'FedraSerifDisplay'"
                  fontSize={{
                    base: '2rem',
                    md: '3.5rem',
                    xl: '5rem',
                    '2xl': '6.5rem',
                  }}
                  marginLeft="auto"
                  marginRight="auto"
                >
                  {section?.[1]?.sectionContent?.[0]?.data?.title5}
                </Heading>
                <Heading
                  className="section-2__scroll-heading"
                  display="inline-flex"
                  color="white"
                  fontFamily="'FedraSerifDisplay'"
                  fontSize={{
                    base: '2rem',
                    md: '3.5rem',
                    xl: '5rem',
                    '2xl': '6.5rem',
                  }}
                  marginLeft="auto"
                  marginRight="5%"
                >
                  Raw
                </Heading>
                <Heading
                  className="section-2__scroll-heading"
                  display="inline-flex"
                  color="white"
                  fontFamily="'FedraSerifDisplay'"
                  fontSize={{
                    base: '2rem',
                    md: '3.5rem',
                    xl: '5rem',
                    '2xl': '6.5rem',
                  }}
                  marginLeft="5%"
                  marginRight="auto"
                >
                  {section?.[1]?.sectionContent?.[0]?.data?.title4}
                </Heading>
                <Heading
                  className="section-2__scroll-heading"
                  display="inline-flex"
                  color="white"
                  fontFamily="'FedraSerifDisplay'"
                  fontSize={{
                    base: '2rem',
                    md: '3.5rem',
                    xl: '5rem',
                    '2xl': '6.5rem',
                  }}
                  marginLeft="auto"
                  marginRight="15%"
                >
                  {section?.[1]?.sectionContent?.[0]?.data?.title1}
                </Heading>
              </Box>
            </Box>
            <Box
              className="section-2__wrapper part-2"
              h={{ md: '100vh' }}
              minH={'100vh'}
              py={20}
              pos={'sticky'}
              top={0}
              display="flex"
              flexDirection="column"
              justifyContent="center"
              bgGradient="linear-gradient(0deg, #17310B 0%, rgba(71, 118, 50, 0) 100%)"
              textAlign="center"
            >
              <Box maxW={600} w={'100%'} mx={'auto'} px={'1.25rem'}>
                <Heading
                  color="white"
                  fontSize={{ base: '3.5rem', xl: '5rem' }}
                  fontFamily="'FedraSerifDisplay'"
                >
                  {section?.[1]?.sectionContent?.[1]?.text}
                </Heading>

                <Text color="#FAEFE7" fontSize={{ base: '18px', lg: '24px' }}>
                  {section?.[1]?.sectionContent?.[2]?.text}
                </Text>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box
          as="section"
          className="section-3"
          h={{ base: '750vh', lg: '1500vh' }}
          pos={'relative'}
        >
          <Box
            h={'1650vh'}
            sx={{
              clipPath:
                'polygon(0% 20vw, 50% 0%, 100% 20vw, 100% 100%, 0% 100%)',
            }}
            backgroundColor={'black'}
          >
            <Box
              className="section-3__bg-wrapper"
              pos="fixed"
              inset="0"
              h={'100vh'}
              w={'100%'}
              zIndex={-2}
            >
              <Image
                className="section-3__bg-img"
                src={`${config.imgPath}/d/46272/1684474039-ib_secion3bg.jpg`}
                pos="absolute"
                inset={0}
                w={'100%'}
                h={'100%'}
                objectFit={'cover'}
                objectPosition={'top'}
              ></Image>

              <Box
                className="section-3__upasaka-imgs"
                pos="absolute"
                bottom={{ md: '0' }}
                top={{ base: '15%', md: 'unset' }}
                left="50%"
                transform={'translateX(-50%)'}
                w={{ base: '100%', md: '100%' }}
                maxH={'95%'}
                sx={{ aspectRatio: 1 }}
                zIndex="1"
              >
                <Image
                  className="section-3__img-upasaka"
                  src={useBreakpointValue({
                    base: `${config.imgPath}/d/46272/1684475469-lb_mob_maa.png`,
                    xl: `${config.imgPath}/d/46272/1684475400-lb_maa.png`,
                  })}
                  alt="maa"
                  pos="absolute"
                  bottom="0"
                  left="49%"
                  transform="translateX(-50%) scale(1.5)"
                  h="80%"
                  maxW="none"
                  objectFit="contain"
                  opacity="0"
                ></Image>
                <Image
                  className="section-3__img-trishul"
                  src={useBreakpointValue({
                    // base: `${config.imgPath}/d/46272/1684477453-lb_mob_trishul.png`,
                    // sm: `${config.imgPath}/d/46272/1690279421-trishul.png`,
                    base: `${config.imgPath}/d/46272/1690279421-trishul.png`,
                  })}
                  alt="lb_trishul"
                  pos="absolute"
                  bottom="0"
                  left="56%"
                  transform="translateX(-50%) scale(2)"
                  h="90%"
                  maxW="none"
                  objectFit="contain"
                  transformOrigin="center top"
                  opacity="0"
                ></Image>
                <Image
                  className="section-3__img-devotee"
                  src={`${config.imgPath}/d/46272/1684473932-lb_sevika.png`}
                  pos="absolute"
                  bottom="0"
                  left="60%"
                  transform="translateX(-50%) scale(1.5)"
                  h="80%"
                  maxW="none"
                  objectFit="contain"
                  opacity="0"
                ></Image>
                <Box
                  pos={'absolute'}
                  left={0}
                  top={'50%'}
                  height={'100vh'}
                  width={'100vw'}
                  bgGradient="linear(#0000 0vw, #000f 63vw, black 100vw)"
                  display={{ base: 'block', md: 'none' }}
                ></Box>
              </Box>

              <Image
                className="section-3__bg-devi-chakras"
                src={`${config.imgPath}/d/46272/1690368100-slide-16_9-63.jpg`}
                pos="absolute"
                inset={0}
                w={'100%'}
                h={'100%'}
                objectFit={'cover'}
              ></Image>

              <Image
                className="section-3__bg-dandams"
                src={`${config.imgPath}/d/46272/1689868424-slide-16_9-15.jpg`}
                pos="absolute"
                inset={0}
                w={'100%'}
                h={'100%'}
                objectFit={{ base: 'contain', md: 'cover' }}
                opacity="0"
                visibility="hidden"
              ></Image>

              <Image
                className="section-3__bg-cover-img"
                src={`${config.imgPath}/d/46272/1684474033-ib_secion5bg.jpeg`}
                pos="absolute"
                inset={0}
                w={'100%'}
                h={'100%'}
                objectFit={'cover'}
                zIndex={-1}
                opacity="0"
                visibility="hidden"
                display={{ base: 'block', md: 'none' }}
              ></Image>
            </Box>

            <Box
              className="section-3__content-wrapper part-1"
              h={{ md: '100vh' }}
              minH={'100vh'}
              py={{ base: 12, xl: 24, '2xl': 32 }}
              // bg={"black"}
              pos={'sticky'}
              top={0}
              display="flex"
              flexDirection="column"
              justifyContent={{
                base: 'flex-end',
                md: 'center',
                xl: 'flex-end',
              }}
            >
              <Box
                className="page-container"
                display={'flex'}
                alignItems={{ md: 'center' }}
                flexDirection={{ base: 'column', md: 'row' }}
              >
                <Box
                  maxW={{ base: '20rem', xl: '27.5rem' }}
                  marginLeft={{ xl: 'auto' }}
                >
                  <Text
                    color="#FAEFE7"
                    fontSize={{ base: '1.25rem', xl: '1.75rem' }}
                    pb={3}
                  >
                    {section?.[2]?.sectionContent?.[0]?.text}
                  </Text>
                  <Heading
                    as="H1"
                    color="#D47A33"
                    fontSize={{ base: '2.875rem', xl: '4.125rem' }}
                    lineHeight={1}
                    fontFamily="'FedraSerifDisplay'"
                  >
                    {section?.[2].sectionContent?.[1]?.text}
                  </Heading>
                </Box>
              </Box>
            </Box>

            <Box
              className="section-3__content-wrapper part-2"
              h={{ md: '100vh' }}
              minH={'100vh'}
              py={{ base: 8, sm: 16, xl: 24, '2xl': 32 }}
              // bg={"black"}
              pos={'sticky'}
              top={0}
              display="flex"
              flexDirection="column"
              justifyContent={{ base: 'flex-end', md: 'center' }}
              textAlign="center"
            >
              <Box className="page-container">
                <Box maxW={{ base: '30rem', xl: '36.5rem' }} margin="auto">
                  <svg
                    width="28"
                    viewBox="0 0 22 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M0.802814 12.0339C0.802814 13.5905 3.92269 17.1229 4.99851 17.1229C5.75159 17.1229 9.08663 13.5905 9.08663 12.6326C9.08663 11.8543 6.71983 9.04039 5.48263 8.20221C5.59022 5.56792 7.90323 3.29286 9.248 1.67637L7.52669 3.8147e-06C4.29923 2.15533 0.802814 7.66338 0.802814 12.0339ZM12.6368 12.0339C12.6368 13.5905 15.7567 17.1229 16.8325 17.1229C17.5856 17.1229 20.9207 13.5905 20.9207 12.6326C20.9207 11.8543 18.5539 9.04039 17.3167 8.20221C17.4242 5.56792 19.7373 3.29286 21.082 1.67637L19.3607 3.8147e-06C16.1333 2.15533 12.6368 7.66338 12.6368 12.0339Z"
                      fill="white"
                    />
                  </svg>
                  <Text
                    color="#F5E576"
                    fontWeight="medium"
                    fontStyle="italic"
                    fontSize={{ base: '1.75rem', xl: '2.1rem' }}
                    mb={8}
                    opacity="0.8"
                  >
                    {section?.[3]?.sectionContent?.[0]?.quote?.replace(
                      '- Sadhguru',
                      ''
                    )}
                  </Text>
                  <Image
                    src={`${config.imgPath}/d/46272/1684475419-lb_sadhguru_signature.png`}
                    alt="sadhguru_Signature"
                    w="7rem"
                    margin="auto"
                  ></Image>
                </Box>
              </Box>
            </Box>

            <Box
              className="section-3__content-wrapper part-3"
              h={{ md: '100vh' }}
              minH={'100vh'}
              py={{ base: 8, sm: 16, xl: 24, '2xl': 32 }}
              pos={'sticky'}
              top={0}
              display="flex"
              flexDirection="column"
              justifyContent={{ base: 'flex-end', md: 'center' }}
              // opacity={0}
              // visibility={"hidden"}
            >
              <Box className="page-container">
                <Box
                  display="flex"
                  textAlign={{ base: 'left', sm: 'right' }}
                  justifyContent="space-between"
                  alignItems="center"
                  gap="2rem"
                  flexDirection={{ base: 'column-reverse', lg: 'row' }}
                >
                  <Box className="section-3__sadhguru-imgs" flex="1">
                    <Image
                      src={`${config.imgPath}/d/46272/1684475449-lb_sadhguru_img3.png`}
                      display="block"
                      w="52%"
                      ml={{ md: '10%' }}
                      pos="relative"
                      zIndex="2"
                    ></Image>
                    <Image
                      src={`${config.imgPath}/d/46272/1684475443-lb_sadhguru_img4.png`}
                      display="block"
                      w="55%"
                      mt={{ base: '-10%', md: '-13%' }}
                      ml="auto"
                      pos="relative"
                      zIndex="1"
                    ></Image>
                    <Image
                      src={`${config.imgPath}/d/46272/1684475438-lb_sadhguru_img5.png`}
                      w="55%"
                      ml={{ base: '5%', md: '0' }}
                      mt={{ base: '-15%', md: '-25%' }}
                      display="block"
                    ></Image>
                  </Box>

                  <Text
                    fontSize={{ base: '1.5rem', md: '1.75rem', xl: '2.4rem' }}
                    maxWidth={{ base: '100%', sm: '29rem' }}
                    alignSelf={{ base: 'flex-end', sm: 'auto' }}
                  >
                    {section?.[4]?.sectionContent?.[0]?.text}
                  </Text>
                </Box>
              </Box>
            </Box>

            <Box
              className="section-3__content-wrapper part-4"
              h={{ md: '100vh' }}
              minH={'100vh'}
              py={{ base: 8, sm: 16, xl: 24, '2xl': 32 }}
              pos={'sticky'}
              top={0}
              display="flex"
              flexDirection="column"
              justifyContent="center"
              textAlign="center"
              // opacity={0}
              // visibility={"hidden"}
            >
              <Box className="page-container">
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  gap="2rem"
                  flexDirection="column"
                >
                  <Text
                    fontSize={{ base: '2.375rem', md: '3.25rem', xl: '4.5rem' }}
                    lineHeight="1"
                    maxWidth="36rem"
                    fontFamily="'FedraSerifDisplay'"
                  >
                    {section?.[5]?.sectionContent?.[0]?.text}
                  </Text>

                  <Box className="section-3__chakra-block" flex="1">
                    <Box
                      className="section-3__chakra-item"
                      padding={{ base: '0.5rem 0', md: '0.75rem 0' }}
                    >
                      <Box></Box>
                      <Image
                        src={`${config.imgPath}/d/46272/1684474084-lb_y_img.png`}
                        display="block"
                        width={{ base: '4.5rem', md: '6rem', xl: '8rem' }}
                      ></Image>
                      <Box display="flex" gap="0.75rem" alignItems="center">
                        <Box h="2px" width="7rem" bg="#FF981F"></Box>
                        <Text
                          color="#FF981F"
                          fontSize={{
                            base: '0.875rem',
                            sm: '1.25rem',
                            lg: '2rem',
                          }}
                        >
                          Anahata
                        </Text>
                      </Box>
                    </Box>

                    <Box
                      className="section-3__chakra-item"
                      padding={{ base: '0.5rem 0', md: '0.75rem 0' }}
                    >
                      <Box
                        display="flex"
                        gap="0.75rem"
                        alignItems="center"
                        flexDir="row-reverse"
                      >
                        <Box h="2px" width="7rem" bg="#FF981F"></Box>
                        <Text
                          color="#FF981F"
                          fontSize={{
                            base: '0.875rem',
                            sm: '1.25rem',
                            lg: '2rem',
                          }}
                        >
                          Manipuraka
                        </Text>
                      </Box>
                      <Image
                        src={`${config.imgPath}/d/46272/1684475462-lb_r_img.png`}
                        display="block"
                        width={{ base: '4.5rem', md: '6rem', xl: '8rem' }}
                      ></Image>
                      <Box></Box>
                    </Box>

                    <Box
                      className="section-3__chakra-item"
                      padding={{ base: '0.5rem 0', md: '0.75rem 0' }}
                    >
                      <Box></Box>
                      <Image
                        src={`${config.imgPath}/d/46272/1684474088-lb_v_img.png`}
                        display="block"
                        width={{ base: '4.5rem', md: '6rem', xl: '8rem' }}
                      ></Image>
                      <Box display="flex" gap="0.75rem" alignItems="center">
                        <Box h="2px" width="7rem" bg="#FF981F"></Box>
                        <Text
                          color="#FF981F"
                          fontSize={{
                            base: '0.875rem',
                            sm: '1.25rem',
                            lg: '2rem',
                          }}
                        >
                          Swadhishthana
                        </Text>
                      </Box>
                    </Box>

                    <Box
                      className="section-3__chakra-item"
                      padding={{ base: '0.5rem 0', md: '0.75rem 0' }}
                    >
                      <Box
                        display="flex"
                        gap="0.75rem"
                        alignItems="center"
                        flexDir="row-reverse"
                      >
                        <Box h="2px" width="7rem" bg="#FF981F"></Box>
                        <Text
                          color="#FF981F"
                          fontSize={{
                            base: '0.875rem',
                            sm: '1.25rem',
                            lg: '2rem',
                          }}
                        >
                          Muladhara
                        </Text>
                      </Box>
                      <Image
                        src={`${config.imgPath}/d/46272/1684475405-lb_l_img.png`}
                        display="block"
                        width={{ base: '4.5rem', md: '6rem', xl: '8rem' }}
                      ></Image>
                      <Box></Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>

            <Box
              className="section-3__content-wrapper part-5"
              h={{ md: '100vh' }}
              minH={'100vh'}
              py={{ base: 8, sm: 16, xl: 24, '2xl': 32 }}
              pos={'sticky'}
              top={0}
              display="flex"
              flexDirection="column"
              justifyContent="center"
              textAlign="center"
              // opacity={0}
              // visibility={"hidden"}
            >
              <Box className="page-container">
                <Text
                  fontSize={{ base: '1.5rem', md: '1.75rem', xl: '2rem' }}
                  maxWidth="43rem"
                  margin="0 auto"
                >
                  {section?.[5]?.sectionContent?.[1]?.text}
                </Text>
              </Box>
            </Box>

            <Box
              className="section-3__content-wrapper part-6"
              h={{ md: '100vh' }}
              minH={'100vh'}
              py={{ base: 8, sm: 16, xl: 24, '2xl': 32 }}
              pos={'sticky'}
              top={0}
              display="flex"
              flexDirection="column"
              justifyContent="center"
              textAlign="center"
            >
              <Box className="page-container">
                <Text
                  fontSize={{ base: '1.75rem', md: '2rem', xl: '2.5rem' }}
                  maxWidth="54rem"
                  margin="0 auto"
                >
                  {section?.[6]?.sectionContent?.[0]?.text}
                </Text>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box
          as="section"
          className="section-4"
          h={{ base: '750vh', lg: '1000vh' }}
          pos={'relative'}
        >
          <Box
            h={'1150vh'}
            sx={{
              clipPath:
                'polygon(0% 20vw, 50% 0%, 100% 20vw, 100% 100%, 0% 100%)',
            }}
            backgroundColor={'black'}
          >
            <Box
              className="section-4__bg-wrapper"
              pos="fixed"
              inset="0"
              h={'100vh'}
              w={'100%'}
              zIndex={-2}
            >
              <Image
                className="section-4__bg-img bg-1"
                src={`${config.imgPath}/d/46272/1684474033-ib_secion5bg.jpeg`}
                pos="absolute"
                inset={0}
                w={'100%'}
                h={'100%'}
                objectFit={'cover'}
              ></Image>

              <Image
                className="section-4__bg-img bg-2"
                src={`${config.imgPath}/d/46272/1684474737-ib_secion10bg.jpg`}
                pos="absolute"
                inset={0}
                w={'100%'}
                h={'100%'}
                objectFit={'cover'}
                opacity="0"
                visibility="hidden"
              ></Image>
            </Box>

            <Box
              className="section-4__content-wrapper part-1"
              h={{ md: '100vh' }}
              minH={'100vh'}
              py={{ base: 8, sm: 16, xl: 24, '2xl': 32 }}
              pos={'sticky'}
              top={0}
              display="flex"
              flexDirection="column"
              justifyContent="center"
              textAlign="center"
              // opacity={0}
              // visibility={"hidden"}
            >
              <Box className="page-container">
                <Text
                  fontSize={{ base: '1.75rem', md: '2rem', xl: '2.625rem' }}
                  maxWidth={{ base: '32rem', xl: '42rem' }}
                  margin="0 auto"
                >
                  {section?.[7]?.sectionContent?.[0]?.text}
                </Text>
              </Box>
            </Box>

            <Box
              className="section-4__content-wrapper part-2"
              h={{ md: '100vh' }}
              minH={'100vh'}
              py={{ base: 8, sm: 16, xl: 24, '2xl': 32 }}
              pos={'sticky'}
              top={0}
              display="flex"
              flexDirection="column"
              justifyContent="center"
              // opacity={0}
              // visibility={"hidden"}
            >
              <Box className="page-container">
                <Box
                  className="section-4__sadhguru-imgs"
                  maxW="78rem"
                  w="100%"
                  mx="auto"
                >
                  <Image
                    src={`${config.imgPath}/d/46272/1684475429-lb_sadhguru_img7.jpg`}
                    display="block"
                    w={{ base: '80%', md: '53%' }}
                    pos="relative"
                    zIndex="2"
                  ></Image>
                  <Image
                    src={`${config.imgPath}/d/46272/1689868413-20121005_chi_0427-e_2-10.jpg`}
                    display="block"
                    w={{ base: '82%', md: '54%' }}
                    mt={{ base: '-10%', md: '-13%' }}
                    ml="auto"
                  ></Image>
                  <Image
                    src={`${config.imgPath}/d/46272/1684475434-lb_sadhguru_img6.jpg`}
                    w={{ base: '70%', md: '46%' }}
                    ml={{ base: '5%', md: '15%' }}
                    mt="-5%"
                    display="block"
                  ></Image>
                </Box>
              </Box>
            </Box>

            <Box
              className="section-4__content-wrapper part-3"
              h={{ md: '100vh' }}
              minH={'100vh'}
              py={{ base: 8, sm: 16, xl: 24, '2xl': 32 }}
              pos={'sticky'}
              top={0}
              display="flex"
              flexDirection="column"
              justifyContent="center"
            >
              <Box className="page-container">
                <Box
                  className="section-4__sadhguru-imgs"
                  maxW="60rem"
                  w="100%"
                  mx="auto"
                >
                  <Button
                    height="auto"
                    width="100%"
                    padding="0"
                    bg="transparent"
                    onClick={onOpen}
                    _hover={{ transform: 'scale(1.025)' }}
                  >
                    <Image
                      src={`${config.imgPath}/d/46272/1684476832-3d-view-with-icon.jpg`}
                      alt="Science Creating Goddesses"
                      display="block"
                      w="100%"
                    ></Image>
                  </Button>
                </Box>
              </Box>

              <Modal
                isOpen={isOpen}
                onClose={onClose}
                size={useBreakpointValue({ base: 'full', md: '6xl' })}
                isCentered
              >
                <ModalOverlay bg="rgba(0, 0, 0, 0.7)" />
                <ModalContent
                  p={0}
                  m="0"
                  bg={{ base: '#000000' }}
                  maxW={{ base: 'auto', md: '992px' }}
                >
                  <ModalCloseButton
                    _hover={{ bg: '#fff', color: '#000' }}
                    _active={{ bg: '#fff', color: '#000' }}
                    _focus={{ bg: '#fff', color: '#000' }}
                    zIndex="20"
                    top={{ base: '40px', md: '-5px' }}
                    bg="#fff"
                    color="#000"
                    right={{ base: '3px', md: '-5px' }}
                    rounded="full"
                    // className={styles.closeButton}
                  />
                  <ModalBody p={0} m="0" w="100%" position="relative">
                    <Box
                      pos={{ base: 'static', md: 'static' }}
                      left="50%"
                      right="50%"
                      // transform={{ md: 'none', base: 'translate(-50%, -50%)' }}
                      display={{ base: 'flex', md: 'block' }}
                      justifyContent={{ base: 'center' }}
                      alignItems={{ base: 'center' }}
                      h={{ base: '648px', md: '648px' }}
                    >
                      <Box
                        bg="#fff"
                        onClick={onClose}
                        w="32px"
                        h="32px"
                        rounded="full"
                        _hover={{ bg: '#fff' }}
                        display={{ base: 'grid', md: 'none' }}
                        placeItems="center"
                        pos="absolute"
                        top="-145px"
                        left="-185px"
                        fontFamily="FedraSansStd-Book"
                        zIndex="10"
                        cursor="pointer"
                        color="black"
                      >
                        x
                      </Box>

                      <Box
                        as="iframe"
                        src={section?.[7]?.sectionContent[1]?.linkUrl}
                        w="100%"
                        h={{ base: '100vh', md: '100%' }}
                      ></Box>
                    </Box>
                  </ModalBody>
                </ModalContent>
              </Modal>
            </Box>

            <Box
              className="section-4__content-wrapper part-4"
              h={{ md: '100vh' }}
              minH={'100vh'}
              py={{ base: 8, sm: 16, xl: 24, '2xl': 32 }}
              pos={'sticky'}
              top={0}
              display="flex"
              flexDirection="column"
              justifyContent="center"
              textAlign="center"
              lineHeight="1.3"
            >
              <Box className="page-container">
                <Box
                  display="grid"
                  gridTemplateColumns={{
                    base: 'minmax(0, 1fr)',
                    lg: 'minmax(0, 1fr) minmax(0, 1fr)',
                  }}
                  gridGap={{ base: '2rem', lg: '5rem' }}
                  alignItems="center"
                >
                  <Text
                    color="#D09700"
                    fontSize={{ base: '1.75rem', md: '2rem', lg: '2.625rem' }}
                    maxWidth={{ base: '25rem', sm: '37.5rem' }}
                    margin="0 auto"
                  >
                    {section?.[8]?.sectionContent?.[0]?.text}
                  </Text>

                  <Image
                    src={`${config.imgPath}/d/46272/1684474074-sciencecreatinggoddessesslide.jpg`}
                    display="block"
                    w="100%"
                    maxW={{ base: '32rem', lg: '100%' }}
                    margin="0 auto"
                  ></Image>
                </Box>
              </Box>
            </Box>

            <Box
              className="section-4__content-wrapper part-5"
              h={{ md: '100vh' }}
              minH={'100vh'}
              py={{ base: 12, xl: 24, '2xl': 32 }}
              pos={'sticky'}
              top={0}
              display="flex"
              flexDirection="column"
              justifyContent={{
                base: 'flex-start',
                md: 'center',
                xl: 'center',
              }}
              textAlign="center"
              lineHeight="1.3"
            >
              <Box
                className="page-container"
                display={'flex'}
                alignItems={{ md: 'center' }}
                flexDirection={{ base: 'column', md: 'row' }}
              >
                <Box maxW="35rem" margin={{ base: '0 auto', xl: '0 0 0 auto' }}>
                  <Text
                    color="#D47A33"
                    fontSize={{ base: '1.75rem', md: '2rem', xl: '2.625rem' }}
                  >
                    {section?.[10]?.sectionContent?.[0]?.text}
                  </Text>
                </Box>
              </Box>
            </Box>

            <Box
              className="section-4__content-wrapper part-6"
              h={{ md: '100vh' }}
              minH={'100vh'}
              py={{ base: 8, sm: 16, xl: 24, '2xl': 32 }}
              pos={'sticky'}
              top={0}
              display="flex"
              flexDirection="column"
              justifyContent="center"
              textAlign={{ base: 'center', md: 'left' }}
              lineHeight="1.3"
            >
              <Box className="page-container">
                <Box
                  display="grid"
                  gridTemplateColumns={{
                    base: 'minmax(0, 1fr)',
                    md: 'minmax(0, 1.2fr) minmax(0, 1fr)',
                  }}
                  gridGap={{ base: '2rem', xl: '5rem' }}
                  alignItems="center"
                >
                  <Box display="flex" flexDir="column" gap="2rem">
                    <Text
                      color="#D09700"
                      fontSize={{ base: '1.75rem', md: '2rem', xl: '2.625rem' }}
                    >
                      {section?.[11]?.sectionContent?.[0]?.text}
                    </Text>
                    <Image
                      className="img-3"
                      // src={`${config.imgPath}/d/46272/1684475429-lb_sadhguru_img7.jpg`}
                      src="/assets/images/devi_img/lb-section-4/part-6-img-1.jpg"
                      display="block"
                      w={{ base: '60%', md: '80%' }}
                      maxW={{ base: '30rem' }}
                      margin={{ base: '0 auto', md: '0' }}
                    ></Image>
                  </Box>

                  <Box display="flex" flexDir="column" gap="2rem">
                    <Image
                      className="img-2"
                      // src={`${config.imgPath}/d/46272/1684475434-lb_sadhguru_img6.jpg`}
                      src="/assets/images/devi_img/lb-section-4/part-6-img-2.jpg"
                      display="block"
                      w={{ base: '60%', md: '100%' }}
                      maxW={{ base: '32rem', lg: '100%' }}
                      margin="0 auto"
                    ></Image>
                    <Image
                      className="img-1"
                      // src={`${config.imgPath}/d/46272/1684475423-lb_sadhguru_img8.jpg`}
                      src="/assets/images/devi_img/lb-section-4/part-6-img-3.jpg"
                      display="block"
                      w={{ base: '60%', md: '100%' }}
                      maxW={{ base: '32rem', md: '100%' }}
                      margin="0 auto"
                      position="relative"
                      left={{ base: '-10%', md: '-70%' }}
                      marginTop={{ base: '-10%', md: '-20%' }}
                      marginBottom="-10%"
                      order={{ base: '-1', md: 'unset' }}
                      zIndex="-1"
                    ></Image>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box
          as="section"
          className="section-5"
          h={{ base: '200vh', lg: '400vh' }}
          pos={'relative'}
        >
          <Box
            h={'550vh'}
            sx={{
              clipPath:
                'polygon(0% 20vw, 50% 0%, 100% 20vw, 100% 100%, 0% 100%)',
            }}
            backgroundColor={'black'}
          >
            <Box
              className="section-5__bg-wrapper"
              pos="fixed"
              inset="0"
              h={'100vh'}
              w={'100%'}
              bg="black"
              zIndex={-2}
            >
              <Image
                className="section-5__bg-img"
                src={useBreakpointValue({
                  base: `${config.imgPath}/d/46272/1684474066-the_source_of_creation_slide_1.jpg`,
                  xl: `${config.imgPath}/d/46272/1690368121-shiva_img.jpg`,
                })}
                pos="absolute"
                inset={0}
                w={'100%'}
                h={'100%'}
                objectFit={'cover'}
                opacity="0.2"
              ></Image>
            </Box>

            <Box
              className="section-5__content-wrapper"
              h={{ md: '100vh' }}
              minH={'100vh'}
              py={{ base: 8, sm: 16, xl: 24, '2xl': 32 }}
              pos={'sticky'}
              top={0}
              display="flex"
              flexDirection="column"
              justifyContent="center"
              textAlign="center"
              lineHeight="1.3"
            >
              <Box className="page-container">
                <Text
                  color="#D09700"
                  fontSize={{ base: '1.75rem', md: '2rem', xl: '2.625rem' }}
                  maxWidth={{ base: '39rem', xl: '48rem' }}
                  margin="0 auto"
                >
                  According to Yogic lore, Shiva or “that which is not” was
                  absolutely still. When Devi, or Shakti, the embodiment of
                  energy, came in contact with Shiva, he came awake and took the
                  form of Rudra - the one who roared.
                </Text>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box
          as="section"
          className="section-6"
          h={{ base: '300vh', lg: '400vh' }}
          pos={'relative'}
        >
          <Box
            h={'550vh'}
            sx={{
              clipPath:
                'polygon(0% 20vw, 50% 0%, 100% 20vw, 100% 100%, 0% 100%)',
            }}
            backgroundColor={'black'}
          >
            <Box
              className="section-6__bg-wrapper"
              pos="fixed"
              inset="0"
              h={'100vh'}
              w={'100%'}
              bg="black"
              zIndex={-2}
            >
              <Image
                className="section-6__bg-img bg-1"
                src={useBreakpointValue({
                  base: `${config.imgPath}/d/46272/1690368091-about_devi_slide_1_m-1.jpg`,
                  lg: `${config.imgPath}/d/46272/1690208204-image-2023-07-24-at-19-45-38-edited.png`,
                })}
                pos="absolute"
                inset={0}
                w={'100%'}
                h={'100%'}
                objectFit={'cover'}
                opacity="0.5"
              ></Image>
              <Image
                className="section-6__bg-img bg-2"
                src={`${config.imgPath}/d/46272/1684474743-ib_secion14bg.jpg`}
                pos="absolute"
                inset={0}
                w={'100%'}
                h={'100%'}
                objectFit={'cover'}
                opacity="0"
                visibility="hidden"
              ></Image>
            </Box>

            <Box
              className="section-6__content-wrapper"
              h={{ md: '100vh' }}
              minH={'100vh'}
              py={{ base: 8, sm: 16, xl: 24, '2xl': 32 }}
              pos={'sticky'}
              top={0}
              display="flex"
              flexDirection="column"
              justifyContent="center"
              textAlign="center"
              lineHeight="1.3"
            >
              <Box className="page-container">
                <Text
                  color="#D09700"
                  fontSize={{ base: '1.75rem', md: '2rem', xl: '2.5rem' }}
                  maxWidth={{ base: '28rem', xl: '38rem' }}
                  margin="0 auto"
                >
                  {section?.[14]?.sectionContent?.[0]?.text}
                </Text>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box as="section" className="section-7" h={'400vh'} pos={'relative'}>
          <Box
            h={'550vh'}
            sx={{
              clipPath:
                'polygon(0% 20vw, 50% 0%, 100% 20vw, 100% 100%, 0% 100%)',
            }}
            backgroundColor={'black'}
          >
            <Box
              className="section-7__bg-wrapper"
              pos="fixed"
              inset="0"
              h={'100vh'}
              w={'100%'}
              bg="black"
              zIndex={-2}
            >
              <Image
                className="section-7__bg-img"
                src="/assets/images/devi_img/Ib_secion17bg-cropped.jpeg"
                // src={`${config.imgPath}/d/46272/1684475590-ib_secion17bg.jpg`}
                pos="absolute"
                inset={0}
                w={'100%'}
                h={'100%'}
                objectFit={'cover'}
                opacity="0"
              ></Image>
            </Box>

            <Box
              className="section-7__content-wrapper part-1"
              h={{ md: '100vh' }}
              minH={'100vh'}
              py={{ base: 8, sm: 16, xl: 24, '2xl': 32 }}
              pos={'sticky'}
              top={0}
              display="flex"
              flexDirection="column"
              justifyContent="center"
              textAlign="center"
              lineHeight="1.3"
            >
              <Box className="page-container">
                <Box maxWidth={{ base: '28rem', xl: '38rem' }} margin="0 auto">
                  <Heading
                    color="common.white"
                    fontSize={{ base: '4rem', xl: '6rem', '2xl': '8.75rem' }}
                    fontFamily="'FedraSerifDisplay'"
                    pb={4}
                  >
                    {section?.[15]?.sectionContent?.[0]?.text}
                  </Heading>
                  <Text
                    color="#F6A15E"
                    fontSize={{ base: '1.75rem', md: '2rem', xl: '2.5rem' }}
                  >
                    {section?.[15]?.sectionContent?.[1]?.text}
                  </Text>
                </Box>
              </Box>
            </Box>

            <Box
              className="section-7__content-wrapper part-2"
              h={{ md: '100vh' }}
              minH={'100vh'}
              py={{ base: 8, sm: 16, xl: 24, '2xl': 32 }}
              pos={'sticky'}
              top={0}
              display="flex"
              flexDirection="column"
              justifyContent="center"
              textAlign="center"
              lineHeight="1.3"
            >
              <Box className="page-container">
                <Box maxWidth={{ base: '28rem', xl: '38rem' }} margin="0 auto">
                  <Heading
                    color="common.white"
                    fontSize={{ base: '4rem', xl: '6rem', '2xl': '8.75rem' }}
                    fontFamily="'FedraSerifDisplay'"
                    pb={4}
                  >
                    {section?.[16]?.sectionContent?.[0]?.text}
                  </Heading>
                  <Text
                    color="#F6A15E"
                    fontSize={{ base: '1.75rem', md: '2rem', xl: '2.5rem' }}
                  >
                    {section?.[16]?.sectionContent?.[1]?.text}
                  </Text>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <style>
        {`
          svg { display: inline-block; }
          @keyframes loadingDotMotion {
            0% {
              transform: scale(1);
              border-width: 8px;
            }
            100% {
              transform: scale(0.4);
              border-width: 48px;
            }
          }

          .page-container {
            max-width: 85rem;
            width: 100%;
            margin-left: auto;
            margin-right: auto;
            padding: 0 1rem;
          }

          
          @media (min-width: 1680px) {
            .page-container {
              max-width: 100rem;
            }
          }

          @media (min-width: 480px) {
            .page-container {
              padding: 0 1.5rem;
            }
          }

          .section-3__chakra-item {
            display: grid;
            align-items: center;
            grid-auto-columns: 1fr;
            grid-column-gap: 0.75rem;
            grid-row-gap: 0.75rem;
            grid-template-columns: minmax(0px, 1fr) auto minmax(0px, 1fr);
            grid-template-rows: auto;
          }
          
          .page-scroll-icon-wrap svg {
            width: 100%;
          }

          @keyframes scrollIconLine {
            0% {
              transform: translateY(-5%);
            }
            100% {
              transform: translateY(5%);
            } 
          }

          .scroll-icon-arrow {
            animation: scrollIconLine 1s linear infinite;
          }
        `}
      </style>

      <PageConfig isLikeVisible={false} pageData={pageData} />
    </WrapLayout>
  );
};

export const getStaticPaths = async () => {
  const lbPaths = config.USE_LOCAL_URL
    ? [`/linga-bhairavi/in/en/about-devi`]
    : await getLandingPaths('L1', '/about-devi', '', '/about-devi-animation');
  return {
    paths: lbPaths,
    fallback: false,
  };
};

export const getStaticProps = async ({
  preview,
  previewData,
  params: { region, lang },
}) => {
  const topMenuData = await fetchHeaderMenuData(
    getLocale(region, lang, 'lb'),
    'lbtopmenu'
  );
  const headerMenuData = await fetchHeaderMenuData(
    getLocale(region, lang, 'lb'),
    'lbmainmenu'
  );
  const footerData = await fetchFooterDetailData(
    getLocale(region, lang, 'lb'),
    'lbfooter'
  );
  const pageData = await fetchDetails(region, lang, `about-devi`, {
    preview,
    data: previewData,
  });

  return {
    props: {
      headerMenuData: headerMenuData?.data || null,
      footerData: footerData?.data || null,
      topMenuData: topMenuData,
      pageData: pageData || null,
      isPreview: preview ? true : false,
      region: region,
      lang: lang,
      menuType: 'lb',
    },
  };
};

export default AboutDevi;
